import React from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { useMutation, useQuery } from 'react-fetching-library';
import {
  Modal,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Stack,
  Alert,
  Button,
  Portal,
  Typography
} from '@mui/material';
import { SmallFormDateField } from '../../../components/small-fields/SmallFormDateField';
import { SmallFormTextField } from '../../../components/small-fields/SmallFormTextField';

import {
  sendAgreementTemplate,
  getAgreementTemplateAction
} from '../../../services/request';
import { formatDate } from '../../../services/functions';

import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
};

export default function AgreementTemplateForm({
  open,
  handleClose,
  setTemplates
}) {
  const { module, season } = useSelector(state => state.app);
  const refInputFile = React.useRef(null);
  const [type, setType] = React.useState('');
  const [fileName, setFileName] = React.useState('');
  const [date, setDate] = React.useState(formatDate(new Date()));
  const [relatedFunctions, setRelatedFunctions] = React.useState([]);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [choices, setChoices] = React.useState([]);
  const { loading, mutate } = useMutation(sendAgreementTemplate);
  const optionsRequest = useQuery(
    getAgreementTemplateAction('OPTIONS', null, module)
  );

  const resetForm = () => {
    setType('');
    setFileName('');
    setDate(formatDate(new Date()));
    setRelatedFunctions([]);
    setErrorMessage('');

    if (refInputFile.current) {
      refInputFile.current.value = null;
    }
  };

  const handleFileSelect = e => {
    if (refInputFile.current.files[0]) {
      setFileName(refInputFile.current.files[0].name);
    } else {
      setFileName(e.target.files[0].name);
    }
  };

  const handleChangeDate = value => {
    setDate(formatDate(value));
  };
  const handleChangeRelatedFunctions = e => setRelatedFunctions(e.target.value);
  const handleChangeType = e => setType(e.target.value);

  const handleSubmit = async () => {
    if (!_.isNil(refInputFile.current.files[0]) && type !== '') {
      const formData = new FormData();

      formData.append('file', refInputFile.current.files[0]);
      formData.append('type', type);
      formData.append('since', date);
      formData.append('season', season);
      relatedFunctions.forEach(value => {
        formData.append('related_functions', value);
      });

      const { error, payload } = await mutate(formData);

      if (!error) {
        if (payload.message !== undefined) {
          setErrorMessage(payload.message);
        } else {
          setTemplates(prev => [...prev, payload]);

          resetForm();
          handleClose();
        }
      } else {
        setErrorMessage(payload?.message || 'Wystąpił błąd');
      }
    }
  };

  React.useEffect(() => {
    if (open === false) {
      setType('');
      setFileName('');
      setDate(formatDate(new Date()));
      setErrorMessage('');
      if (refInputFile.current != null) {
        refInputFile.current.value = null;
      }
    }
  }, [open]);

  React.useEffect(() => {
    if (!optionsRequest.loading && !optionsRequest.error) {
      setChoices(optionsRequest.payload.actions.GET.choices);
    }
  }, [optionsRequest.loading]);

  return (
    <Portal>
      <Modal open={open} onClose={handleClose}>
        <Card sx={{ ...style, width: '500px' }}>
          <CardHeader
            title="Dodaj szablon"
            action={
              <IconButton color="primary" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            }
          />
          <CardContent>
            <Stack
              spacing={3}
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <SmallFormTextField
                select
                name="type"
                placeholder="Rodzaj szablonu"
                label="Rodzaj szablonu"
                onChange={handleChangeType}
                value={type}
                sx={{ width: '100%' }}
                options={choices.template_type}
              />
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  variant="contained"
                  component="label"
                  sx={{ textAlign: 'center' }}
                >
                  Wczytaj plik
                  <input
                    type="file"
                    accept=".odt"
                    hidden
                    ref={refInputFile}
                    onChange={handleFileSelect}
                  />
                </Button>
                {fileName !== '' ? (
                  <Stack direction="row" spacing={1}>
                    <InsertDriveFileOutlinedIcon color="primary" />
                    <Typography variant="subtitle2">{fileName}</Typography>
                  </Stack>
                ) : null}
              </Stack>
              <SmallFormTextField
                select
                multiple
                name="related_functions"
                placeholder="Dotyczy stanowisk"
                label="Dotyczy stanowisk"
                onChange={handleChangeRelatedFunctions}
                value={relatedFunctions}
                sx={{ width: '100%' }}
                options={choices.related_functions}
              />
              <SmallFormDateField
                value={date}
                onChange={handleChangeDate}
                label="Szablon obowiązuje od"
                sx={{ width: '100%' }}
              />
              {errorMessage !== '' ? (
                <Alert
                  variant="outlined"
                  severity="error"
                  sx={{ width: '100%' }}
                >
                  {errorMessage}
                </Alert>
              ) : null}
              <Button
                variant="contained"
                fullWidth
                onClick={handleSubmit}
                disabled={loading}
              >
                zapisz
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Modal>
    </Portal>
  );
}
